/**
 * Shared width math for the two lateral panels (Files on the left, AI chat on the right).
 *
 * Each panel used to clamp itself to 80vw in isolation, which is fine while only one exists. With
 * both open the centre column can be squeezed to nothing — a 1366px laptop restoring a 600px chat
 * next to a 420px files panel leaves 346px of content. These helpers make the clamp *joint*: a panel
 * may never grow past what is left after the other panel and a minimum readable centre.
 *
 * When the centre is collapsed (Files + Chat fill the row), the centre floor is 0 and the 80vw cap
 * is lifted so the shared divider can assign any split that keeps both panels above
 * {@link PANEL_MIN_WIDTH}.
 */

/** Narrowest a side panel may be dragged or squeezed. */
export const PANEL_MIN_WIDTH = 350

/**
 * While the centre is visible, a drag that would leave less than this hides the centre entirely.
 * Also the floor used by clamp/reconcile during normal (non-restore) layout.
 */
export const COLLAPSE_CENTER_BELOW = 350

/**
 * When the centre is brought back (top-nav click), panels are re-clamped so the centre gets at
 * least this much room — larger than {@link COLLAPSE_CENTER_BELOW} so Results/Dashboard are usable.
 */
export const RESTORE_CENTER_WIDTH = 600

export interface ClampOptions {
  /** Width of the *other* lateral panel, or 0 when it is closed. */
  otherPanelWidth?: number
  /** Injectable for tests; defaults to the live viewport. */
  viewportWidth?: number
  /** When true, the centre may shrink to 0 and the 80vw cap is not applied. */
  centerCollapsed?: boolean
  /**
   * Minimum centre width while the centre is visible. Defaults to
   * {@link COLLAPSE_CENTER_BELOW}; pass {@link RESTORE_CENTER_WIDTH} when expanding from collapse.
   */
  minCenterWidth?: number
}

/**
 * Clamp a requested panel width into `[PANEL_MIN_WIDTH, budget]`.
 *
 * With the centre visible, the budget is the smaller of 80% of the viewport and whatever is left
 * after the other panel and the centre floor. With the centre collapsed, the budget is whatever
 * remains after the other panel (no 80vw cap).
 */
export function clampPanelWidth(requested: number, opts: ClampOptions = {}): number {
  const viewportWidth = opts.viewportWidth ?? window.innerWidth
  const otherPanelWidth = opts.otherPanelWidth ?? 0
  const centerCollapsed = opts.centerCollapsed === true
  const minCenter = centerCollapsed
    ? 0
    : opts.minCenterWidth ?? COLLAPSE_CENTER_BELOW

  const budgetMax = viewportWidth - otherPanelWidth - minCenter
  // Math.max keeps max >= min so the clamp never inverts on a narrow viewport, where the budget can
  // go negative. Below 992px the SCSS stacks the panels vertically anyway, so the floor is harmless.
  const max = centerCollapsed
    ? Math.max(PANEL_MIN_WIDTH, budgetMax)
    : Math.max(PANEL_MIN_WIDTH, Math.min(viewportWidth * 0.8, budgetMax))

  return Math.max(PANEL_MIN_WIDTH, Math.min(max, requested))
}

export interface PanelState {
  width: number
  open: boolean
}

/**
 * Re-clamp both panels together, deterministically: Files resolves first, then the chat against the
 * *new* Files width. Closed panels keep their stored width untouched so the user's preferred size
 * comes back intact once the other panel closes.
 *
 * When `centerCollapsed` and both panels are open, widths are scaled to fill the viewport.
 */
export function reconcileWidths(
  files: PanelState,
  chat: PanelState,
  viewportWidth: number = window.innerWidth,
  centerCollapsed: boolean = false,
  minCenterWidth: number = COLLAPSE_CENTER_BELOW
): {filesWidth: number; chatWidth: number} {
  if (centerCollapsed && files.open && chat.open) {
    return splitFullWidth(files.width, viewportWidth, files.width + chat.width)
  }

  const filesWidth = files.open
    ? clampPanelWidth(files.width, {
        otherPanelWidth: chat.open ? chat.width : 0,
        viewportWidth,
        centerCollapsed,
        minCenterWidth,
      })
    : files.width

  const chatWidth = chat.open
    ? clampPanelWidth(chat.width, {
        otherPanelWidth: files.open ? filesWidth : 0,
        viewportWidth,
        centerCollapsed,
        minCenterWidth,
      })
    : chat.width

  return {filesWidth, chatWidth}
}

export interface PanelPair {
  filesWidth: number
  chatWidth: number
}

/**
 * Resolve one step of dragging a side panel while the other side panel is open and the centre is
 * visible. Space is taken in order: first from the centre down to {@link COLLAPSE_CENTER_BELOW},
 * then from the opposite panel down to {@link PANEL_MIN_WIDTH}. Only when both are at their floor
 * does the drag `collapse` the centre, returning a full-row split at the pointer.
 *
 * `otherStartWidth` is the opposite panel's width when the drag began, so dragging back within the
 * same gesture lets a squeezed panel grow back to where it was.
 */
export function resolvePairedDrag(
  side: 'left' | 'right',
  requested: number,
  otherStartWidth: number,
  rowWidth: number
): {collapse: boolean} & PanelPair {
  const dragged = Math.max(PANEL_MIN_WIDTH, Math.round(requested))
  const otherPreferred = Math.max(PANEL_MIN_WIDTH, otherStartWidth)
  const other = Math.min(otherPreferred, Math.round(rowWidth) - dragged - COLLAPSE_CENTER_BELOW)

  if (other < PANEL_MIN_WIDTH) {
    const filesRaw = side === 'left' ? requested : rowWidth - requested
    return {collapse: true, ...splitFullWidth(filesRaw, rowWidth)}
  }

  return side === 'left'
    ? {collapse: false, filesWidth: dragged, chatWidth: other}
    : {collapse: false, filesWidth: other, chatWidth: dragged}
}

/**
 * Split the full row between Files and Chat (centre collapsed).
 *
 * `filesRaw` is the desired Files width (e.g. pointer x from the row's left edge). When `preferTotal`
 * is supplied (reconcile path), the Files share is taken as a fraction of that total so a window
 * resize preserves the user's ratio.
 */
export function splitFullWidth(
  filesRaw: number,
  viewportWidth: number = window.innerWidth,
  preferTotal?: number
): {filesWidth: number; chatWidth: number} {
  let desired = filesRaw
  if (preferTotal !== undefined && preferTotal > 0) {
    desired = (filesRaw / preferTotal) * viewportWidth
  }

  const filesWidth = Math.max(
    PANEL_MIN_WIDTH,
    Math.min(viewportWidth - PANEL_MIN_WIDTH, Math.round(desired))
  )
  // Round the viewport before subtracting so both widths stay integers (HiDPI / subpixel
  // rect.width values otherwise leave a fractional chatWidth that parseInt truncates on reload).
  return {filesWidth, chatWidth: Math.round(viewportWidth) - filesWidth}
}
